import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { ShieldCheck, ShieldX, Download } from "lucide-react";

export default function Verify() {
  const { certificateId } = useParams();
  const { t, lang } = useI18n();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    api
      .verifyCertificate(certificateId)
      .then((data) => setState({ loading: false, data }))
      .catch(() => setState({ loading: false, data: null }));
  }, [certificateId]);

  const valid = !!state.data;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="verify-page">
      <div className="mx-auto max-w-2xl px-6 md:px-10 py-16 md:py-24">
        <Link to="/" className="inline-flex items-center gap-2 font-black text-xl tracking-tight">
          <span className="inline-block h-8 w-8 rounded-2xl bg-[#4361EE] grid place-items-center text-white shadow-[0_4px_0_#2A3EB1]">
            <span className="text-lg">✦</span>
          </span>
          <span>{lang === "ar" ? "سباركل" : "Sparkl"}</span>
        </Link>

        {state.loading ? (
          <div className="mt-10 h-64 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
        ) : (
          <div
            className={`mt-10 rounded-[2.5rem] p-8 md:p-10 border-2 ${
              valid
                ? "bg-gradient-to-br from-[#DCFCE7] to-[#DFF6FD] border-[#166534]/20"
                : "bg-gradient-to-br from-[#FEE2E2] to-[#FEF3C7] border-[#991B1B]/20"
            }`}
            data-testid={valid ? "verify-valid" : "verify-invalid"}
          >
            <div
              className={`inline-flex items-center gap-2 rounded-full text-white font-black px-3 py-1 text-xs uppercase tracking-wider ${
                valid ? "bg-[#166534]" : "bg-[#991B1B]"
              }`}
            >
              {valid ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldX className="h-3.5 w-3.5" />}
              {valid ? t("verify.valid") : t("verify.invalid")}
            </div>

            <h1 className="mt-4 text-3xl md:text-4xl font-black tracking-tight">{t("verify.title")}</h1>

            {valid && (
              <>
                <div className="mt-8 grid gap-3">
                  <Field label={t("verify.studentName")} value={state.data.student_name} testid="verify-name" />
                  <Field
                    label={t("verify.course")}
                    value={lang === "ar" ? state.data.course_title_ar : state.data.course_title_en}
                    testid="verify-course"
                  />
                  <Field
                    label={t("verify.score")}
                    value={`${state.data.score_percent}%`}
                    testid="verify-score"
                  />
                  <Field
                    label={t("verify.issued")}
                    value={new Date(state.data.issued_at).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")}
                    testid="verify-issued"
                  />
                  <Field label={t("verify.certId")} value={state.data.certificate_id} testid="verify-cert-id" mono />
                </div>

                <div className="mt-6">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${state.data.certificate_id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-3 text-sm shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
                    data-testid="verify-download"
                  >
                    <Download className="h-4 w-4" /> {t("certs.download")}
                  </a>
                </div>
              </>
            )}

            <div className="mt-10 text-xs font-bold text-black/50 tracking-wider">
              {t("verify.poweredBy")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, testid, mono }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-black/5 px-4 py-3" data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-wider text-black/50">{label}</div>
      <div className={`mt-0.5 font-bold text-[#1A1A1A] ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}
